import * as z from 'zod';

export const QueryAccountCertificationsRequest = /*#__PURE__*/ z.object({
  query: z
    .intersection(
      z.object({
        filter: z
          .record(z.string(), z.any())
          .describe(
            'Filter object in the following format:\n`"filter" : {\n"fieldName1": "value1",\n"fieldName2":{"$operator":"value2"}\n}`\nExample of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`'
          )
          .optional()
          .nullable(),
        sort: z
          .array(
            z.object({
              fieldName: z
                .string()
                .describe('Name of the field to sort by.')
                .max(50)
                .optional(),
              order: z.enum(['ASC', 'DESC']).optional(),
            })
          )
          .max(4)
          .optional(),
      }),
      z.xor([
        z.object({ cursorPaging: z.never().optional() }),
        z.object({
          cursorPaging: z
            .object({
              limit: z
                .number()
                .int()
                .describe('Number of items to load.')
                .min(0)
                .max(100)
                .optional()
                .nullable(),
              cursor: z
                .string()
                .describe(
                  "Pointer to the next or previous page in the list of results.\n\nYou can get the relevant cursor token\nfrom the `pagingMetadata` object in the previous call's response.\nNot relevant for the first request."
                )
                .max(16000)
                .optional()
                .nullable(),
            })
            .describe(
              'Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`.'
            ),
        }),
      ])
    )
    .describe(
      'Query options. See [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) for more information.'
    ),
});
export const QueryAccountCertificationsResponse = /*#__PURE__*/ z.object({
  accountCertifications: z
    .array(
      z.object({
        _id: z
          .string()
          .describe('Account certification ID.')
          .regex(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            'Must be a valid GUID'
          )
          .optional()
          .nullable(),
        revision: z
          .string()
          .regex(/^-?\d+$/, 'Must be a valid Int64 string')
          .describe(
            'Revision number, which increments by 1 each time the account certification is updated.\nTo prevent conflicting changes,\nthe current revision must be passed when updating the account certification.\n\nIgnored when creating an account certification.'
          )
          .optional()
          .nullable(),
        _createdDate: z
          .date()
          .describe('Date and time the account certification was created.')
          .optional()
          .nullable(),
        _updatedDate: z
          .date()
          .describe('Date and time the account certification was last updated.')
          .optional()
          .nullable(),
        userCertificationIds: z.array(z.string()).max(100).optional(),
        certificationType: z
          .enum([
            'VELO',
            'WEB_DESIGNER',
            'ACCESSIBILITY',
            'STUDIO_DEVELOPER',
            'STUDIO_DESIGN_LEAGUE',
            'STUDIO_DEV_WEBSITES_LEAGUE',
          ])
          .describe(
            "Type of certification the account holds. Set when the certification is created and can't be changed afterward."
          )
          .optional(),
      })
    )
    .optional(),
  pagingMetadata: z
    .object({
      count: z
        .number()
        .int()
        .describe('Number of items returned in the response.')
        .optional()
        .nullable(),
      cursors: z
        .object({
          next: z
            .string()
            .describe('Cursor pointing to next page in the list of results.')
            .max(16000)
            .optional()
            .nullable(),
          prev: z
            .string()
            .describe(
              'Cursor pointing to previous page in the list of results.'
            )
            .max(16000)
            .optional()
            .nullable(),
        })
        .describe('Offset that was requested.')
        .optional(),
      hasNext: z
        .boolean()
        .describe(
          'Indicates if there are more results after the current page.\nIf `true`, another page of results can be retrieved.\nIf `false`, this is the last page.'
        )
        .optional()
        .nullable(),
    })
    .describe('Paging metadata.')
    .optional(),
});
export const ListAccountCertificationsRequest = /*#__PURE__*/ z.object({
  accountId: z
    .string()
    .describe('ID of the account to get certifications for.')
    .regex(
      /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
      'Must be a valid GUID'
    ),
});
export const ListAccountCertificationsResponse = /*#__PURE__*/ z.object({
  certifications: z
    .array(
      z.object({
        certificationType: z
          .enum([
            'VELO',
            'WEB_DESIGNER',
            'ACCESSIBILITY',
            'STUDIO_DEVELOPER',
            'STUDIO_DESIGN_LEAGUE',
            'STUDIO_DEV_WEBSITES_LEAGUE',
          ])
          .describe('Type of certification the account holds.')
          .optional(),
        _createdDate: z
          .date()
          .describe('Date and time the account certification was created.')
          .optional()
          .nullable(),
      })
    )
    .optional(),
});
